import { datadogRum } from '@datadog/browser-rum';
import { ref, watch } from 'vue';

export type GoogleSignInButtonText = 'signin_with' | 'signup_with' | 'continue_with' | 'signin';

export interface GoogleRenderButtonOptions {
  containerHtmlElementId: string;
  text: GoogleSignInButtonText;
  callback: (googleJwt: string) => void;
}

class GoogleSignInService {
  private initialized = ref(false);
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private signInCallback: (googleJwt: string) => void = () => {};

  private googleSignIn = (googleJwt: google.accounts.id.CredentialResponse) => {
    this.signInCallback(googleJwt.credential);
  };

  initialize(): void {
    const observer = new MutationObserver((_, observer) => {
      if (window?.google?.accounts) {
        this.callGoogle(() =>
          window?.google?.accounts?.id?.initialize({
            client_id: process.env.GOOGLE_CLIENT_ID as string,
            callback: this.googleSignIn,
          }),
        );

        this.initialized.value = true;
        observer.disconnect();
      }
    });

    observer.observe(document, { attributes: true, childList: true, subtree: true });
  }

  renderGoogleSignInButton(params: GoogleRenderButtonOptions): void {
    this.signInCallback = params.callback;

    this.waitForGoogleSignInInit(() => {
      const googleButtonContainer = document.getElementById(params.containerHtmlElementId);

      // 1. render google sign in with initial width fitting the parent container
      const initialWidth = googleButtonContainer!.offsetWidth;
      this.callGoogle(() =>
        window?.google?.accounts?.id?.renderButton(googleButtonContainer!, {
          text: params.text,
          type: 'standard',
          width: initialWidth,
        }),
      );

      // 2. watch parent container resizes and adjust google sign in button width accordingly
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // 3. disconnect observer if button is removed from the DOM to avoid memory leaks
          if (!googleButtonContainer!.isConnected) {
            observer.disconnect();
            return;
          }

          const newWidth = Math.floor(entry.contentRect.width);
          this.callGoogle(() =>
            window?.google?.accounts?.id?.renderButton(googleButtonContainer!, {
              text: params.text,
              type: 'standard',
              width: newWidth,
            }),
          );
        }
      });

      observer.observe(googleButtonContainer!);
    });
  }

  signOut(): void {
    window?.google?.accounts?.id?.disableAutoSelect();
  }

  // Google Identity Services is a cross-origin script, so an exception it throws inside these synchronous
  // observer callbacks would reach Datadog only as a bare "Script error.", which the RUM filter drops.
  // Catching it here reports the real message and stack instead.
  private callGoogle(call: () => void): void {
    try {
      call();
    } catch (error) {
      datadogRum.addError(error);
    }
  }

  private waitForGoogleSignInInit(callback: () => void): void {
    const stopWatcher = watch(
      this.initialized,
      (googleSignInInitialized) => {
        if (googleSignInInitialized) {
          callback();

          setTimeout(() => stopWatcher(), 50);
        }
      },
      { immediate: true },
    );
  }
}

export default new GoogleSignInService();
